import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { WizardOption } from 'src/app/models/wizard/wizard-option';
import { WizardStep } from 'src/app/models/wizard/wizard-step';
import { AnalyticsService, AnalyticsUtility } from 'src/app/services/analytics/analytics.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { WizardStepName } from 'src/app/services/wizard/wizard-step-name.enum';
import { WizardService } from 'src/app/services/wizard/wizard.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-need-to-run-step',
  templateUrl: './need-to-run-step.component.html',
  styleUrls: ['./need-to-run-step.component.scss']
})
export class NeedToRunStepComponent implements OnInit {
  currentStep: WizardStep = new WizardStep();
  stepNumber: number = 0;
  title: string = "";
  subtitleBeginning: string = "";
  subtitleMiddle: string = "";
  subtitleEnd: string = "";
  i18nSubtitle: string = "";
  isEnglish: boolean = true;
  needToRunOptions: WizardOption[] = [];
  isContinueBtnDisabled: boolean = true;
  isEducation: boolean = false;
  analyticsStepName: string = AnalyticsUtility.wizardStepName.needToRun;

  constructor(
    private analyticsService: AnalyticsService,
    public wizardService: WizardService,
    private route: ActivatedRoute,
    private confSvc: ConfigService,
    private utilityService: UtilityService) { }

  async ngOnInit(): Promise<void> {
    await this.confSvc.setup();

    this.currentStep = this.wizardService.getStep(WizardStepName.NeedToRun);
    this.stepNumber = this.wizardService.getStepNumber(this.currentStep.name);
    this.title = this.confSvc.getString("needToRunTitle");
    this.subtitleBeginning = this.confSvc.getString("wizardStepSubtitleBeginning");
    this.subtitleMiddle = this.confSvc.getString("multipleSelectionWizardStepSubtitleMiddle");
    this.subtitleEnd = this.confSvc.getString("multipleSelectionWizardStepSubtitleEnd");
    this.i18nSubtitle = this.confSvc.getString("i18nMultipleSelectionWizardStepSubtitle");

    this.isEnglish = this.utilityService.getLocaleInfo().isEnglish;

    this.route.queryParams.subscribe(params => {
      this.wizardService.setSelectedOptionsFromUrlIfPresent(params);
      this.setNeedToRunOptions();
    });

    this.analyticsService.logCurrentPage(this.analyticsStepName);
  }

  setNeedToRunOptions(): void {
    this.wizardService.getSelectedWizardOptionsForStep(WizardStepName.PrimaryUse)
      .pipe(
        switchMap((primaryUseOptions: WizardOption[]) => {
          const primaryUseTitle = primaryUseOptions[0]?.title ? primaryUseOptions[0].title : "";
          this.isEducation = primaryUseTitle === this.confSvc.getString("primaryUseEducationTitle");

          return this.wizardService.getSelectedWizardOptionsForStep(this.currentStep.name);
        })
      )
      .subscribe((needToRunOptions: WizardOption[]) => {
        this.isContinueBtnDisabled = needToRunOptions.length === 0;

        if (this.isEducation) {
          this.buildEducationNeedToRunOptions(needToRunOptions)
        } else {
          this.buildEnterpriseNeedToRunOptions(needToRunOptions)
        }
      });
  }

  buildEducationNeedToRunOptions(selectedOptions:  WizardOption[]) {
    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunBrowsingOptionEduTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEdu"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunBrowsingOptionEduTitle"),
        subtitle: this.confSvc.getString("needToRunBrowsingOptionEduSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-envelope',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunBrowsingOptionEduTitle"))
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunClassworkOptionEduTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEdu"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunClassworkOptionEduTitle"),
        subtitle: this.confSvc.getString("needToRunClassworkOptionEduSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-window-restore',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunClassworkOptionEduTitle"))
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunConferencingOptionEduTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEdu"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunConferencingOptionEduTitle"),
        subtitle: this.confSvc.getString("needToRunConferencingOptionEduSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-video',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunConferencingOptionEduTitle"))
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunContentCreationOptionEduTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEdu"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunContentCreationOptionEduTitle"),
        subtitle: this.confSvc.getString("needToRunContentCreationOptionEduSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-edit',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunContentCreationOptionEduTitle"))
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunVirtualizedOptionEduTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEdu"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunVirtualizedOptionEduTitle"),
        subtitle: this.confSvc.getString("needToRunVirtualizedOptionEduSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-desktop',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunVirtualizedOptionEduTitle"))
      }));
    }
  }

  buildEnterpriseNeedToRunOptions(selectedOptions:  WizardOption[]) {
    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunBrowsingOptionEnterpriseTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEnterprise"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunBrowsingOptionEnterpriseTitle"),
        subtitle: this.confSvc.getString("needToRunBrowsingOptionEnterpriseSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-window-maximize',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunBrowsingOptionEnterpriseTitle"))
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunConferencingOptionEnterpriseTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEnterprise"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunConferencingOptionEnterpriseTitle"),
        subtitle: this.confSvc.getString("needToRunConferencingOptionEnterpriseSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-video',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunConferencingOptionEnterpriseTitle"))
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunProductivityOptionEnterpriseTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEnterprise"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunProductivityOptionEnterpriseTitle"),
        subtitle: this.confSvc.getString("needToRunProductivityOptionEnterpriseSubtitle"),
        iconFontSet: 'fab',
        iconName: 'fa-google',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunProductivityOptionEnterpriseTitle"))
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunGooglePlayOptionEnterpriseTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEnterprise"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunGooglePlayOptionEnterpriseTitle"),
        subtitle: this.confSvc.getString("needToRunGooglePlayOptionEnterpriseSubtitle"),
        iconFontSet: 'fab',
        iconName: 'fa-google-play',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunGooglePlayOptionEnterpriseTitle"))
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunVirtualizationOptionEnterpriseTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEnterprise"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunVirtualizationOptionEnterpriseTitle"),
        subtitle: this.confSvc.getString("needToRunVirtualizationOptionEnterpriseSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-desktop',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunVirtualizationOptionEnterpriseTitle"))
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunParallelsOptionEnterpriseTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEnterprise"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunParallelsOptionEnterpriseTitle"),
        subtitle: this.confSvc.getString("needToRunParallelsOptionEnterpriseSubtitle"),
        iconFontSet: 'fas',
        iconName: 'fa-pause',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunParallelsOptionEnterpriseTitle"))
      }));
    }

    if(this.wizardService.shouldDisplayWizardOption(this.confSvc.getString("needToRunLinuxOptionEnterpriseTitle"), this.confSvc.getStringArray("needToRunOptionsToDisplayEnterprise"))) {
      this.needToRunOptions.push(Object.assign(new WizardOption(), {
        step: this.currentStep,
        title: this.confSvc.getString("needToRunLinuxOptionEnterpriseTitle"),
        subtitle: this.confSvc.getString("needToRunLinuxOptionEnterpriseSubtitle"),
        iconFontSet: 'fab',
        iconName: 'fa-linux',
        isSelected: selectedOptions.some(option => option.title === this.confSvc.getString("needToRunLinuxOptionEnterpriseTitle"))
      }));
    }
  }

  onSelect(event: WizardOption): void {
    this.needToRunOptions.map(item => {
      if(item.title === event.title) {
        item.isSelected = event.isSelected;
        if (event.isSelected) {
          this.wizardService.addSelectedWizardOption(event);
        } else {
          this.wizardService.removeSelectedWizardOption(item.title);
        }
      }
    });

    this.isContinueBtnDisabled = !this.needToRunOptions.some(item => {
      return item.isSelected === true;
    });

  }

}
